import { useState } from "react";
import { trackEvent } from "../utils/analytics";

type Status = "idle" | "loading" | "success" | "error";

/**
 * Shared async state machine for all 4 ZP-55 enquiry forms (Supplier,
 * Franchise, B2B, Contact) — ZP-60's own AC mandates the identical
 * reference-number-on-success behavior across all of them from day one, so
 * this is extracted up front rather than waiting for a third duplicate.
 *
 * `formName` also drives ZP-76's funnel tracking (form_submit/form_complete)
 * — centralized here rather than in each of the 4 call sites, since they'd
 * otherwise all need the identical pair of trackEvent calls.
 */
export default function useEnquiryForm(formName: string) {
  const [status, setStatus] = useState<Status>("idle");
  const [referenceNumber, setReferenceNumber] = useState<string | null>(null);

  const submit = async (fn: () => Promise<{ ok: true; referenceNumber: string }>) => {
    setStatus("loading");
    trackEvent("form_submit", { form_name: formName });
    try {
      const result = await fn();
      setReferenceNumber(result.referenceNumber);
      setStatus("success");
      trackEvent("form_complete", { form_name: formName });
    } catch {
      setStatus("error");
    }
  };

  const reset = () => {
    setStatus("idle");
    setReferenceNumber(null);
  };

  return { status, referenceNumber, submit, reset };
}
